<!-- =========================================================
     ONLINE MODE — Google Autocomplete search
     ========================================================= -->
<ng-container *ngIf="mode === 'online'">
  <form [formGroup]="addressForm" id="form-address">
    <mat-form-field>
      <input
        #addressInput
        [mask]="currentMask"
        [dropSpecialCharacters]="false"
        matInput
        placeholder="{{ 'TypeCepOrAddress' | translate }}"
        formControlName="address"
        [required]="!question.elective"
        [matAutocomplete]="auto"
        (keyup)="doSearch(addressInput)"
      />

      <mat-autocomplete #auto="matAutocomplete">
        <!-- Loading indicator -->
        <mat-option *ngIf="loading" disabled style="pointer-events: none">
          <div class="spinner-container">
            <mat-spinner [diameter]="20" [mode]="'indeterminate'"></mat-spinner>
            {{ "searching..." | translate }}
          </div>
        </mat-option>

        <!-- No results -->
        <mat-option *ngIf="!loading && !addressList.length" disabled>
          {{ "No result" | translate }}
        </mat-option>

        <!-- Address suggestions -->
        <mat-option
          *ngFor="let address of addressList"
          [value]="address.description"
          style="line-height: 19px"
          (click)="selectAddress(address)"
        >
          {{ address.description }}
        </mat-option>

        <!-- Always last: "I can't find my address" -->
        <mat-option
          *ngIf="!loading"
          class="cant-find-option"
          (click)="enterOfflineMode()"
        >
          {{ "I can't find my address" | translate }}
        </mat-option>
      </mat-autocomplete>

      <mat-error *ngIf="addressForm.invalid && addressForm.dirty">
        {{ "Select an address" | translate }}
      </mat-error>
    </mat-form-field>

    <p
      class="custom-error"
      *ngIf="
        addressForm.controls.address.dirty &&
        addressForm.controls.addtional_information.invalid
      "
    >
      {{ "Select an address" | translate }}
    </p>
  </form>

  <!-- API error banner -->
  <div class="api-error-container" *ngIf="apiError">
    <span class="api-error-message">
      {{ "Error searching address" | translate }}
    </span>
    <div class="api-error-actions">
      <button mat-stroked-button (click)="retryOnlineMode(); doSearch()">
        {{ "Try again" | translate }}
      </button>
      <button mat-stroked-button (click)="enterOfflineMode()">
        {{ "Fill manually" | translate }}
      </button>
    </div>
  </div>

  <!-- Address detail fields (shown after a successful selection) -->
  <div class="extra" *ngIf="showAdd">
    <ng-container *ngTemplateOutlet="detailFields"></ng-container>
  </div>
</ng-container>

<!-- =========================================================
     OFFLINE MODE — Manual entry with CEP lookup
     ========================================================= -->
<ng-container *ngIf="mode === 'offline'">
  <div class="offline-mode">

    <!-- Header: back to search -->
    <div class="offline-header">
      <button class="def-button" type="button" (click)="retryOnlineMode()">
        &larr; {{ "Back to search" | translate }}
      </button>
    </div>

    <div class="extra">
      <!-- Postal code is always FIRST in offline mode -->
      <div class="postal-code-row">
        <mat-form-field
          class="postal-code-field"
          [class.mat-form-field-invalid]="!fieldValidations['postalCode']"
        >
          <mat-label>{{ "Postal code" | translate }}</mat-label>
          <input
            matInput
            [(ngModel)]="additional_info.postalCode"
            placeholder="{{ 'Postal code' | translate }}"
            (ngModelChange)="onPostalCodeInput($event); onFieldChange()"
            [mask]="isBrazilianAddress() ? '00000-000' : null"
            [dropSpecialCharacters]="false"
            [attr.inputmode]="isBrazilianAddress() ? 'numeric' : null"
            required
          />
          <mat-spinner
            *ngIf="cepLoading"
            matSuffix
            [diameter]="18"
          ></mat-spinner>
          <mat-error *ngIf="!fieldValidations['postalCode'] && !fieldErrorKeys['postalCode']">
            {{ requiredFields["postalCode"] | translate }}
            {{ "is required" | translate }}.
          </mat-error>
        </mat-form-field>
        <p class="field-format-error" *ngIf="fieldErrorKeys['postalCode']">
          {{ fieldErrorKeys['postalCode'] | translate }}
        </p>

        <p
          class="cep-success-message"
          *ngIf="!cepLoading && additional_info.resolvedVia === 'viacep'"
        >
          &#10003; {{ "Address found via postal code" | translate }}
        </p>

        <p class="cep-loading-message" *ngIf="cepLoading">
          {{ "Looking up postal code..." | translate }}
        </p>
      </div>

      <!-- Rest of address fields (street → reference point, postal code excluded) -->
      <ng-container *ngTemplateOutlet="detailFieldsWithoutPostal"></ng-container>
    </div>
  </div>
</ng-container>

<!-- =========================================================
     SHARED TEMPLATES
     ========================================================= -->

<!-- Full detail fields (used by online-mode showAdd) -->
<ng-template #detailFields>
  <mat-form-field
    [class.mat-form-field-invalid]="!fieldValidations['streetName']"
  >
    <mat-label>{{ "Street name" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.streetName"
      placeholder="{{ 'Street name' | translate }}"
      (ngModelChange)="onFieldChange()"
      required
    />
    <mat-error *ngIf="!fieldValidations['streetName'] && !fieldErrorKeys['streetName']">
      {{ requiredFields["streetName"] | translate }}
      {{ "is required" | translate }}.
    </mat-error>
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['streetName']">
    {{ fieldErrorKeys['streetName'] | translate }}
  </p>

  <mat-form-field
    [class.mat-form-field-invalid]="!fieldValidations['streetNumber']"
  >
    <mat-label>{{ "Street number" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.streetNumber"
      placeholder="{{ 'Street number' | translate }}"
      (ngModelChange)="onFieldChange()"
      required
    />
    <mat-error *ngIf="!fieldValidations['streetNumber'] && !fieldErrorKeys['streetNumber']">
      {{ requiredFields["streetNumber"] | translate }}
      {{ "is required" | translate }}.
    </mat-error>
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['streetNumber']">
    {{ fieldErrorKeys['streetNumber'] | translate }}
  </p>

  <mat-form-field [class.mat-form-field-invalid]="!fieldValidations['unitNumber']">
    <mat-label>{{ "Unit / Apartment Number" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.unitNumber"
      placeholder="{{ 'Unit / Apartment Number' | translate }}"
      (ngModelChange)="onFieldChange()"
    />
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['unitNumber']">
    {{ fieldErrorKeys['unitNumber'] | translate }}
  </p>

  <mat-form-field [class.mat-form-field-invalid]="!fieldValidations['buildingName']">
    <mat-label>{{ "Building Name" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.buildingName"
      placeholder="{{ 'Building Name' | translate }}"
      (ngModelChange)="onFieldChange()"
    />
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['buildingName']">
    {{ fieldErrorKeys['buildingName'] | translate }}
  </p>

  <mat-form-field [class.mat-form-field-invalid]="!fieldValidations['referencePoint']">
    <mat-label>{{ "Reference Point" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.referencePoint"
      placeholder="{{ 'Reference Point' | translate }}"
      (ngModelChange)="onFieldChange()"
    />
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['referencePoint']">
    {{ fieldErrorKeys['referencePoint'] | translate }}
  </p>

  <mat-form-field [class.mat-form-field-invalid]="!fieldValidations['neighborhood']">
    <mat-label>{{ "Neighborhood" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.neighborhood"
      placeholder="{{ 'Neighborhood' | translate }}"
      (ngModelChange)="onFieldChange()"
    />
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['neighborhood']">
    {{ fieldErrorKeys['neighborhood'] | translate }}
  </p>

  <mat-form-field [class.mat-form-field-invalid]="!fieldValidations['city']">
    <mat-label>{{ "City" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.city"
      placeholder="{{ 'City' | translate }}"
      (ngModelChange)="onFieldChange()"
      required
    />
    <mat-error *ngIf="!fieldValidations['city'] && !fieldErrorKeys['city']">
      {{ requiredFields["city"] | translate }} {{ "is required" | translate }}.
    </mat-error>
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['city']">
    {{ fieldErrorKeys['city'] | translate }}
  </p>

  <mat-form-field
    [class.mat-form-field-invalid]="!fieldValidations['state']"
  >
    <mat-label>{{ "State" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.state"
      placeholder="{{ 'State' | translate }}"
      (ngModelChange)="onFieldChange()"
      required
    />
    <mat-error *ngIf="!fieldValidations['state'] && !fieldErrorKeys['state']">
      {{ requiredFields["state"] | translate }}
      {{ "is required" | translate }}.
    </mat-error>
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['state']">
    {{ fieldErrorKeys['state'] | translate }}
  </p>

  <mat-form-field *ngIf="showCounty" [class.mat-form-field-invalid]="!fieldValidations['county']">
    <mat-label>{{ "County" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.county"
      placeholder="{{ 'County' | translate }}"
      (ngModelChange)="onFieldChange()"
    />
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['county']">
    {{ fieldErrorKeys['county'] | translate }}
  </p>

  <mat-form-field
    [class.mat-form-field-invalid]="!fieldValidations['country']"
  >
    <mat-label>{{ "Country" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.country"
      placeholder="{{ 'Country' | translate }}"
      (ngModelChange)="onFieldChange()"
      required
    />
    <mat-error *ngIf="!fieldValidations['country'] && !fieldErrorKeys['country']">
      {{ requiredFields["country"] | translate }}
      {{ "is required" | translate }}.
    </mat-error>
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['country']">
    {{ fieldErrorKeys['country'] | translate }}
  </p>

  <mat-form-field
    [class.mat-form-field-invalid]="!fieldValidations['postalCode']"
  >
    <mat-label>{{ "Postal code" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.postalCode"
      placeholder="{{ 'Postal code' | translate }}"
      (ngModelChange)="onFieldChange()"
      [mask]="isBrazilianAddress() ? '00000-000' : null"
      [dropSpecialCharacters]="false"
      [attr.inputmode]="isBrazilianAddress() ? 'numeric' : null"
      required
    />
    <mat-error *ngIf="!fieldValidations['postalCode'] && !fieldErrorKeys['postalCode']">
      {{ requiredFields["postalCode"] | translate }}
      {{ "is required" | translate }}.
    </mat-error>
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['postalCode']">
    {{ fieldErrorKeys['postalCode'] | translate }}
  </p>
</ng-template>

<!-- Detail fields WITHOUT postal code (used by offline mode, which puts CEP first) -->
<ng-template #detailFieldsWithoutPostal>
  <mat-form-field
    [class.mat-form-field-invalid]="!fieldValidations['streetName']"
  >
    <mat-label>{{ "Street name" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.streetName"
      placeholder="{{ 'Street name' | translate }}"
      (ngModelChange)="onFieldChange()"
      required
    />
    <mat-error *ngIf="!fieldValidations['streetName'] && !fieldErrorKeys['streetName']">
      {{ requiredFields["streetName"] | translate }}
      {{ "is required" | translate }}.
    </mat-error>
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['streetName']">
    {{ fieldErrorKeys['streetName'] | translate }}
  </p>

  <mat-form-field
    [class.mat-form-field-invalid]="!fieldValidations['streetNumber']"
  >
    <mat-label>{{ "Street number" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.streetNumber"
      placeholder="{{ 'Street number' | translate }}"
      (ngModelChange)="onFieldChange()"
      required
    />
    <mat-error *ngIf="!fieldValidations['streetNumber'] && !fieldErrorKeys['streetNumber']">
      {{ requiredFields["streetNumber"] | translate }}
      {{ "is required" | translate }}.
    </mat-error>
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['streetNumber']">
    {{ fieldErrorKeys['streetNumber'] | translate }}
  </p>

  <mat-form-field [class.mat-form-field-invalid]="!fieldValidations['unitNumber']">
    <mat-label>{{ "Unit / Apartment Number" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.unitNumber"
      placeholder="{{ 'Unit / Apartment Number' | translate }}"
      (ngModelChange)="onFieldChange()"
    />
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['unitNumber']">
    {{ fieldErrorKeys['unitNumber'] | translate }}
  </p>

  <mat-form-field [class.mat-form-field-invalid]="!fieldValidations['buildingName']">
    <mat-label>{{ "Building Name" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.buildingName"
      placeholder="{{ 'Building Name' | translate }}"
      (ngModelChange)="onFieldChange()"
    />
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['buildingName']">
    {{ fieldErrorKeys['buildingName'] | translate }}
  </p>

  <mat-form-field [class.mat-form-field-invalid]="!fieldValidations['referencePoint']">
    <mat-label>{{ "Reference Point" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.referencePoint"
      placeholder="{{ 'Reference Point' | translate }}"
      (ngModelChange)="onFieldChange()"
    />
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['referencePoint']">
    {{ fieldErrorKeys['referencePoint'] | translate }}
  </p>

  <mat-form-field [class.mat-form-field-invalid]="!fieldValidations['neighborhood']">
    <mat-label>{{ "Neighborhood" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.neighborhood"
      placeholder="{{ 'Neighborhood' | translate }}"
      (ngModelChange)="onFieldChange()"
    />
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['neighborhood']">
    {{ fieldErrorKeys['neighborhood'] | translate }}
  </p>

  <mat-form-field [class.mat-form-field-invalid]="!fieldValidations['city']">
    <mat-label>{{ "City" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.city"
      placeholder="{{ 'City' | translate }}"
      (ngModelChange)="onFieldChange()"
      required
    />
    <mat-error *ngIf="!fieldValidations['city'] && !fieldErrorKeys['city']">
      {{ requiredFields["city"] | translate }} {{ "is required" | translate }}.
    </mat-error>
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['city']">
    {{ fieldErrorKeys['city'] | translate }}
  </p>

  <mat-form-field
    [class.mat-form-field-invalid]="!fieldValidations['state']"
  >
    <mat-label>{{ "State" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.state"
      placeholder="{{ 'State' | translate }}"
      (ngModelChange)="onFieldChange()"
      required
    />
    <mat-error *ngIf="!fieldValidations['state'] && !fieldErrorKeys['state']">
      {{ requiredFields["state"] | translate }}
      {{ "is required" | translate }}.
    </mat-error>
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['state']">
    {{ fieldErrorKeys['state'] | translate }}
  </p>

  <mat-form-field *ngIf="showCounty" [class.mat-form-field-invalid]="!fieldValidations['county']">
    <mat-label>{{ "County" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.county"
      placeholder="{{ 'County' | translate }}"
      (ngModelChange)="onFieldChange()"
    />
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['county']">
    {{ fieldErrorKeys['county'] | translate }}
  </p>

  <mat-form-field
    [class.mat-form-field-invalid]="!fieldValidations['country']"
  >
    <mat-label>{{ "Country" | translate }}</mat-label>
    <input
      matInput
      [(ngModel)]="additional_info.country"
      placeholder="{{ 'Country' | translate }}"
      (ngModelChange)="onFieldChange()"
      required
    />
    <mat-error *ngIf="!fieldValidations['country'] && !fieldErrorKeys['country']">
      {{ requiredFields["country"] | translate }}
      {{ "is required" | translate }}.
    </mat-error>
  </mat-form-field>
  <p class="field-format-error" *ngIf="fieldErrorKeys['country']">
    {{ fieldErrorKeys['country'] | translate }}
  </p>
</ng-template>
